import type { ElementType, ReactNode } from 'react';

import { cn } from '@/lib/cn';

export function Container({ className, children }: { className?: string; children: ReactNode }) {
  return <div className={cn('mx-auto w-full max-w-7xl px-4 sm:px-6 lg:px-8', className)}>{children}</div>;
}

export interface SectionProps {
  id?: string;
  className?: string;
  containerClassName?: string;
  /** Subtle raised background to separate adjacent sections. */
  tone?: 'default' | 'raised';
  as?: ElementType;
  children: ReactNode;
}

export function Section({
  id,
  className,
  containerClassName,
  tone = 'default',
  as: Tag = 'section',
  children,
}: SectionProps) {
  return (
    <Tag
      id={id}
      className={cn(
        // `overflow-x-clip` (not `hidden`) contains decorative glows that bleed
        // past the viewport without creating a scroll container, so `sticky`
        // children such as the order summary keep working.
        'scroll-mt-24 overflow-x-clip py-16 sm:py-20 lg:py-24',
        tone === 'raised' && 'bg-ink-soft/70 border-line/60 border-y',
        className,
      )}
    >
      <Container className={containerClassName}>{children}</Container>
    </Tag>
  );
}

export interface SectionHeadingProps {
  eyebrow?: string;
  title: ReactNode;
  description?: ReactNode;
  align?: 'left' | 'center';
  className?: string;
}

export function SectionHeading({ eyebrow, title, description, align = 'center', className }: SectionHeadingProps) {
  return (
    <div className={cn('flex flex-col gap-4', align === 'center' ? 'items-center text-center' : 'items-start', className)}>
      {eyebrow ? (
        <span className="text-brand-bright text-xs font-semibold tracking-[0.28em] uppercase">{eyebrow}</span>
      ) : null}
      <h2 className="text-3xl leading-[1.05] sm:text-4xl lg:text-5xl">{title}</h2>
      {description ? (
        <p className={cn('text-content-muted max-w-2xl text-base sm:text-lg', align === 'center' && 'mx-auto')}>
          {description}
        </p>
      ) : null}
    </div>
  );
}
